import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { type FC, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import DatePicker from "@via/components/date-picker/date-picker.tsx";
import { requiredValidation } from "@via/utils/form-validators.tsx";
import {
  CreatePersonalAccessTokenRequestBody,
  GENERAL_DEFAULT_ACCESS_SCOPE,
  MCP_ACCESS_SCOPES,
  MCP_DEFAULT_ACCESS_SCOPE,
  PersonalAccessTokenScope,
  PersonalAccessTokenUsage,
} from "common";
import { ACCESS_SCOPE_LABELS } from "@via/pages/account/profile/personal-access-tokens/components/access-scope-badge";
import { useCreatePersonalAccessToken } from "@via/pages/account/profile/personal-access-tokens/personal-access-tokens.api";
import { Spinner } from "@via/components/loading/loading.tsx";
import {
  MessageBanner,
  BannerType,
} from "@via/components/message-banner/message-banner.tsx";

interface CreatePersonalAccessTokenFormProps {
  setOpen: (open: boolean) => void;
  onSuccess?: (
    token: string,
    expiresAt: string,
    usageType: PersonalAccessTokenUsage,
    scope: PersonalAccessTokenScope | null
  ) => void;
}

const ACCESS_SCOPE_DESCRIPTIONS: Record<PersonalAccessTokenScope, string> = {
  [PersonalAccessTokenScope.Read]: "View runs, results, datasets and metadata.",
  [PersonalAccessTokenScope.Write]:
    "Also launch runs, upload files and edit records.",
  [PersonalAccessTokenScope.Admin]:
    "Also manage your own tokens and password, and administer this installation if your account is an administrator.",
};

const CreatePersonalAccessTokenForm: FC<CreatePersonalAccessTokenFormProps> = ({
  setOpen,
  onSuccess,
}) => {
  const handleClose = () => setOpen(false);

  const { control, handleSubmit, register, setValue, watch } =
    useForm<CreatePersonalAccessTokenRequestBody>({
      mode: "onChange",
      defaultValues: {
        name: "",
        expiresAt: undefined,
        usageType: PersonalAccessTokenUsage.General,
        scope: GENERAL_DEFAULT_ACCESS_SCOPE,
      },
    });

  // The preselected access level follows the usage until the person picks one; a pick the new
  // usage does not allow (admin for an AI assistant token) moves to that usage's default.
  const usageType = watch("usageType");
  const isMcp = usageType === PersonalAccessTokenUsage.MCP;
  const scopePicked = useRef(false);
  useEffect(() => {
    const current = watch("scope");
    const allowed = isMcp
      ? MCP_ACCESS_SCOPES
      : Object.values(PersonalAccessTokenScope);
    if (!scopePicked.current || !current || !allowed.includes(current)) {
      setValue(
        "scope",
        isMcp ? MCP_DEFAULT_ACCESS_SCOPE : GENERAL_DEFAULT_ACCESS_SCOPE
      );
    }
  }, [isMcp]);

  const {
    mutateAsync: createPersonalAccessToken,
    data: createPersonalAccessTokenResponse,
    isPending: isCreatePersonalAccessTokenLoading,
    isSuccess: isCreatePersonalAccessTokenSuccess,
  } = useCreatePersonalAccessToken();

  const handleCreatePersonalAccessToken = async (
    payload: CreatePersonalAccessTokenRequestBody
  ) => {
    await createPersonalAccessToken(payload);
  };

  useEffect(() => {
    if (
      isCreatePersonalAccessTokenSuccess &&
      createPersonalAccessTokenResponse
    ) {
      if (onSuccess) {
        onSuccess(
          createPersonalAccessTokenResponse.token,
          createPersonalAccessTokenResponse.expiresAt,
          createPersonalAccessTokenResponse.usageType,
          createPersonalAccessTokenResponse.scope
        );
      }
      handleClose();
    }
  }, [isCreatePersonalAccessTokenSuccess, createPersonalAccessTokenResponse]);

  const onSubmit = handleSubmit(handleCreatePersonalAccessToken);

  // Calculate min and max dates for validation
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              New Personal Access Token
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Get started by filling in the information below to create your new
              personal access token.
            </p>
          </div>
        </div>
        <div className="px-4 sm:px-6">
          <MessageBanner
            messages="This token will be shown only once. Make sure to copy it immediately after creation."
            type={BannerType.Info}
            title="Important"
          />
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <FormInputComponent
                control={control}
                name="name"
                label="Token Name"
                placeholder="e.g., My API Token"
                validations={requiredValidation("Token Name")}
                showRequiredField
              />
              <div>
                <DatePicker
                  control={control}
                  name="expiresAt"
                  label="Expiration Date (Optional)"
                  popoverDirection="down"
                  asSingle={true}
                  useRange={false}
                  minDate={tomorrow}
                  maxDate={oneYearFromNow}
                  placeholder="Select expiration date"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Default: 30 days. Maximum: 1 year from today.
                </p>
              </div>
              <fieldset>
                <legend className="text-sm font-medium leading-6 text-gray-900">
                  Token Usage
                </legend>
                <div className="mt-2 space-y-4">
                  <div className="relative flex items-start">
                    <div className="absolute flex h-6 items-center">
                      <input
                        {...register("usageType")}
                        id="usageType-general"
                        type="radio"
                        value={PersonalAccessTokenUsage.General}
                        aria-describedby="usageType-general-description"
                        className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                    </div>
                    <div className="pl-7 text-sm leading-6">
                      <label
                        htmlFor="usageType-general"
                        className="font-medium text-gray-900">
                        General Purpose
                      </label>
                      <p
                        id="usageType-general-description"
                        className="text-gray-500">
                        Standard token for API access
                      </p>
                    </div>
                  </div>
                  <div className="relative flex items-start">
                    <div className="absolute flex h-6 items-center">
                      <input
                        {...register("usageType")}
                        id="usageType-mcp"
                        type="radio"
                        value={PersonalAccessTokenUsage.MCP}
                        aria-describedby="usageType-mcp-description"
                        className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                    </div>
                    <div className="pl-7 text-sm leading-6">
                      <label
                        htmlFor="usageType-mcp"
                        className="font-medium text-gray-900">
                        MCP (Model Context Protocol)
                      </label>
                      <p
                        id="usageType-mcp-description"
                        className="text-gray-500">
                        Token for use with Foundry Connect MCP server in AI
                        assistants
                      </p>
                    </div>
                  </div>
                </div>
              </fieldset>
              <fieldset>
                <legend className="text-sm font-medium leading-6 text-gray-900">
                  Access
                </legend>
                <p className="mt-1 text-xs text-gray-500">
                  Choose the narrowest access that works. A request that needs
                  more is refused.
                </p>
                <div className="mt-2 space-y-4">
                  {Object.values(PersonalAccessTokenScope).map(scope => {
                    const disabled =
                      isMcp && !MCP_ACCESS_SCOPES.includes(scope);
                    return (
                      <div key={scope} className="relative flex items-start">
                        <div className="absolute flex h-6 items-center">
                          <input
                            {...register("scope", {
                              onChange: () => {
                                scopePicked.current = true;
                              },
                            })}
                            id={`scope-${scope}`}
                            type="radio"
                            value={scope}
                            disabled={disabled}
                            aria-describedby={`scope-${scope}-description`}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 disabled:opacity-50"
                          />
                        </div>
                        <div className="pl-7 text-sm leading-6">
                          <label
                            htmlFor={`scope-${scope}`}
                            className={
                              disabled
                                ? "font-medium text-gray-400"
                                : "font-medium text-gray-900"
                            }>
                            {ACCESS_SCOPE_LABELS[scope]}
                          </label>
                          <p
                            id={`scope-${scope}-description`}
                            className="text-gray-500">
                            {ACCESS_SCOPE_DESCRIPTIONS[scope]}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {isMcp && (
                  <p className="mt-2 text-xs text-gray-500">
                    AI assistant tokens can have read or write access, not
                    admin.
                  </p>
                )}
              </fieldset>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end gap-2 px-4 py-4">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={isCreatePersonalAccessTokenLoading}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
          <Spinner loading={isCreatePersonalAccessTokenLoading}>
            Create Token
          </Spinner>
        </button>
      </div>
    </form>
  );
};

export default CreatePersonalAccessTokenForm;
